// Inbound-Draft spec 2026-11-03 §6: the backend answers forbidden operations with
// `{"detail"}` + header `X-Error-Code` (copied into the body as `error_code` by
// src/services/apiErrorCode.ts). Older backends / the bulk endpoint only carry the
// code inside the detail text, hence the substring fallback.
export const KNOWN_INBOUND_ERROR_CODES = [
  'FORBIDDEN_STATE',
  'CANCEL_REFUSED_CHECKED_IN',
  'CANCEL_PENDING',
  'WAREHOUSE_INSTANCE_INVALID',
  'INBOUND_WITHOUT_LINES',
] as const;

export type InboundErrorCode = (typeof KNOWN_INBOUND_ERROR_CODES)[number];

type ErrorLike = { error_code?: unknown; detail?: unknown; message?: unknown };

const detailOf = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const { detail, message } = error as ErrorLike;
  if (typeof detail === 'string' && detail) return detail;
  if (typeof message === 'string' && message) return message;
  return undefined;
};

export function errorCodeOf(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const { error_code: code } = error as ErrorLike;
  if (typeof code === 'string' && code) return code;
  const detail = detailOf(error);
  if (!detail) return undefined;
  return KNOWN_INBOUND_ERROR_CODES.find(known => detail.includes(known));
}

export function inboundErrorMessage(
  error: unknown,
  t: (key: string) => string,
  fallbackKey = 'inbound.operation_failed',
): string {
  const code = errorCodeOf(error);
  if (code && (KNOWN_INBOUND_ERROR_CODES as readonly string[]).includes(code)) {
    return t(`inbound.error_${code.toLowerCase()}`);
  }
  const detail = detailOf(error);
  return detail ? `${t(fallbackKey)}: ${detail}` : t(fallbackKey);
}
